import { Link } from "react-router-dom";
import { ShieldCheck, ScanSearch, FileText, Radio, Check, ArrowRight, Crown, Lock, Gauge, Fingerprint, Zap } from "lucide-react";

const HERO = "https://static.prod-images.emergentagent.com/jobs/8ba4af56-9de3-4da3-b004-66a22e625f64/images/00fef3c095eca1cd80a4532e81c42a80ac3bcb1cef042db735e2d84d42ab105e.jpeg";
const TEXTURE = "https://static.prod-images.emergentagent.com/jobs/8ba4af56-9de3-4da3-b004-66a22e625f64/images/0734e4790ff75b25edcb97774364de32534a227408813f6df49ef1a113bfef57.jpeg";

const STATS = [
  { v: "110", l: "NIST 800-171 controls scored" },
  { v: "40+", l: "policies generated instantly" },
  { v: "80%", l: "cheaper than consultants" },
  { v: "24/7", l: "continuous audit readiness" },
];

const FEATURES = [
  { icon: ScanSearch, title: "Automated Gap Analysis", kicker: "See the truth in minutes",
    desc: "Our AI ingests your policies, cloud configs, and training records and scores you against all 110 NIST 800-171 controls (320 assessment objectives) — every verdict backed by cited, defensible evidence." },
  { icon: FileText, title: "AI Policy Generator", kicker: "40+ documents, done",
    desc: "Generate every required policy and procedure — tailored to your org chart and stack, each cross-referenced to the exact control it satisfies. Weeks of consultant work, in seconds." },
  { icon: Radio, title: "Continuous Evidence Collector", kicker: "Always audit-ready",
    desc: "A lightweight agent captures timestamped, hash-verified evidence automatically — so you don't scramble before an assessment. You're ready every single day." },
];

const MOAT = [
  { icon: Fingerprint, title: "Legally defensible by design", desc: "Every AI verdict is grounded in your own evidence with a citation and hash — no hallucinations, no hand-waving. Built to survive a C3PAO and their lawyers." },
  { icon: Gauge, title: "From negative score to pass", desc: "Most contractors sit at a negative SPRS score and don't know it. We show you exactly where you stand and the fastest, cheapest path to a passing number." },
  { icon: Zap, title: "Outpace the primes' deadlines", desc: "DFARS 7012/7021 is landing in re-competes now. Get compliant before your competitors even finish reading the rule — and win the contracts they can't." },
];

const TIERS = [
  { id: "gap_only", name: "Gap Only", price: "$750", tag: "Know exactly where you stand", featured: false,
    items: ["Full SPRS score (-203 to +110)", "All 110 controls assessed", "Auto-generated POA&M", "Cited evidence per control"] },
  { id: "readiness", name: "Readiness", price: "$2,000", tag: "Replaces a ~$90k consultant", featured: true,
    items: ["Everything in Gap Only", "40+ AI-generated policies", "Continuous Evidence Collector", "Quarterly expert review"] },
  { id: "audit_concierge", name: "Audit Concierge", price: "$5,000", tag: "C3PAO-ready, white glove", featured: false,
    items: ["Everything in Readiness", "Monthly named expert review", "C3PAO assessment prep & liaison", "Priority support"] },
];

export default function Landing() {
  return (
    <div className="bg-white text-brand">
      {/* Nav */}
      <header className="border-b border-white/10 sticky top-0 bg-brand/95 backdrop-blur z-30">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5 text-white">
            <ShieldCheck className="h-7 w-7 text-brand-accent" />
            <span className="font-display font-black text-xl tracking-tight">CMMC Shield</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login" className="text-sm font-medium text-slate-300 hover:text-white px-2 sm:px-3 py-2 whitespace-nowrap" data-testid="nav-login">Sign in</Link>
            <Link to="/score" className="text-sm font-semibold bg-brand-accent text-white px-3 sm:px-4 py-2 rounded-md hover:brightness-110 transition whitespace-nowrap" data-testid="nav-register">Get your score free</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-brand text-white overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover object-right opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand via-brand/90 to-transparent" />
        <div className="relative max-w-6xl mx-auto px-6 py-28 lg:py-36">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand-accent border border-brand-accent/30 rounded-full px-3 py-1 mb-6">
              <Crown className="h-3.5 w-3.5" /> The category leader in CMMC readiness
            </div>
            <h1 className="font-display font-black text-4xl sm:text-6xl tracking-tight leading-[1.03]">
              Don&rsquo;t just pass CMMC.<br /><span className="text-brand-accent">Dominate it.</span>
            </h1>
            <p className="text-lg text-slate-300 mt-6 max-w-xl">
              The AI compliance platform that makes small defense contractors audit-ready faster, cheaper, and more defensibly than any consultant alive — and wins the contracts your competitors can't touch.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-9">
              <Link to="/score" className="bg-brand-accent text-white px-7 py-3.5 rounded-md font-semibold hover:brightness-110 transition flex items-center gap-2 shadow-lg shadow-brand-accent/20" data-testid="hero-cta">
                Get your free SPRS score <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="#pricing" className="px-7 py-3.5 rounded-md font-semibold border border-white/20 text-white hover:bg-white/10 transition">See pricing</a>
            </div>
            <div className="text-xs text-slate-500 font-mono mt-8">NIST SP 800-171 Rev 2 · DFARS 252.204-7012 / -7019 / -7020 / -7021 · SPRS</div>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-brand-secondary text-white border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
          {STATS.map((s) => (
            <div key={s.l} className="px-4 py-8 text-center">
              <div className="font-display font-black text-3xl sm:text-4xl text-brand-accent">{s.v}</div>
              <div className="text-xs text-slate-400 mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-14">
          <div className="overline mb-3">What you get</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">Everything a $150k engagement gives you — automated.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {FEATURES.map((f) => (
            <div key={f.title} className="group border border-slate-200 rounded-lg p-8 bg-white hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <div className="h-12 w-12 rounded-lg bg-brand text-brand-accent flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <f.icon className="h-6 w-6" />
              </div>
              <div className="text-xs font-semibold uppercase tracking-wider text-brand-accent mb-2">{f.kicker}</div>
              <h3 className="font-display font-bold text-lg tracking-tight mb-2">{f.title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Moat / why #1 */}
      <section className="relative bg-brand text-white overflow-hidden">
        <img src={TEXTURE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="relative max-w-6xl mx-auto px-6 py-24">
          <div className="max-w-2xl mb-14">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand-accent mb-4"><Lock className="h-3.5 w-3.5" /> Why we're #1</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">Built to overachieve what you didn't know you needed.</h2>
            <p className="text-slate-300 mt-4">Horizontal tools weren't built for the defense industrial base. We were — and it shows in the one thing that matters when an assessor is in the room: defensibility.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {MOAT.map((m) => (
              <div key={m.title} className="border border-white/10 rounded-lg p-7 bg-white/5 backdrop-blur-sm hover:bg-white/10 transition">
                <m.icon className="h-7 w-7 text-brand-accent mb-4" />
                <h3 className="font-display font-bold text-lg tracking-tight mb-2">{m.title}</h3>
                <p className="text-slate-300 text-sm leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-slate-50 border-y border-slate-200 py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="overline mb-3">Pricing</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">Consultant-grade outcomes. Software pricing.</h2>
            <p className="text-slate-500 mt-3">Undercut human consultants by up to 80% — with results they can't match.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 items-start">
            {TIERS.map((t) => (
              <div key={t.id} data-testid={`tier-${t.id}`}
                className={`rounded-lg p-8 bg-white transition-all duration-300 ${t.featured ? "border-2 border-brand-accent shadow-xl md:-translate-y-2" : "border border-slate-200 shadow-sm hover:shadow-lg"}`}>
                {t.featured && <div className="inline-flex items-center gap-1 text-xs font-bold text-brand-accent uppercase tracking-wider mb-2"><Crown className="h-3.5 w-3.5" /> Most popular</div>}
                <h3 className="font-display font-bold text-xl tracking-tight">{t.name}</h3>
                <p className="text-sm text-slate-500 mb-4">{t.tag}</p>
                <div className="mb-6"><span className="font-display font-black text-4xl">{t.price}</span><span className="text-slate-500">/mo</span></div>
                <ul className="space-y-2.5 mb-7">
                  {t.items.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm text-slate-700"><Check className="h-4 w-4 text-success mt-0.5 shrink-0" /> {it}</li>
                  ))}
                </ul>
                <Link to="/register" className={`block text-center py-3 rounded-md font-semibold transition ${t.featured ? "bg-brand text-white hover:bg-brand-secondary" : "border border-slate-300 hover:bg-slate-50"}`}>
                  Start now
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-brand text-white">
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tight leading-tight">Your competitors are already scoring.<br />Are you?</h2>
          <p className="text-slate-300 mt-5 max-w-xl mx-auto">Get your real SPRS score in minutes — free, no credit card. Then decide how far ahead of the pack you want to be.</p>
          <Link to="/score" className="inline-flex items-center gap-2 bg-brand-accent text-white px-8 py-4 rounded-md font-semibold hover:brightness-110 transition mt-9 shadow-lg shadow-brand-accent/20">
            Get your free score <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <footer className="bg-brand-secondary text-slate-400 text-center text-sm py-10 px-6 border-t border-white/10">
        <div className="flex items-center justify-center gap-2 mb-2 text-white"><ShieldCheck className="h-5 w-5 text-brand-accent" /><span className="font-display font-bold">CMMC Shield</span></div>
        Built for SMB defense contractors · Not affiliated with the DoD or CMMC-AB.
      </footer>
    </div>
  );
}
